/**
 * Pure planner for the in-scene update after an element was moved to a new
 * region set on the backend. Regions that are not loaded in the scene need no
 * action, and a loaded region the element newly joins while it also stays in
 * another loaded region is left alone too: both pick the element up from the
 * regenerated GLTF on their next load (the mesh cannot be safely cloned here).
 *
 * @param regionsWithInstance loaded regions that currently hold an instance of the element
 * @param loadedRegions       all regions currently loaded in the scene
 * @param oldRegionIds        region set before the move
 * @param newRegionIds        region set after the move
 */
export interface RecompartmentActions {
  /** Detach the instance from `from` (without disposing) and attach it to `to`. */
  rehome: Array<{ from: string; to: string }>
  /** Remove and dispose the instance living in this region. */
  dispose: string[]
}

export function computeRecompartmentActions(
  regionsWithInstance: string[],
  loadedRegions: string[],
  oldRegionIds: string[],
  newRegionIds: string[],
): RecompartmentActions {
  const oldSet = new Set(oldRegionIds ?? [])
  const newSet = new Set(newRegionIds ?? [])
  const withInstance = new Set(regionsWithInstance ?? [])
  const loaded = new Set(loadedRegions ?? [])

  const removedWithInstance = [...oldSet].filter(r => !newSet.has(r) && withInstance.has(r))
  const addedLoadedMissing = [...newSet].filter(r => !oldSet.has(r) && loaded.has(r) && !withInstance.has(r))

  const actions: RecompartmentActions = { rehome: [], dispose: [] }
  while (removedWithInstance.length > 0 && addedLoadedMissing.length > 0) {
    actions.rehome.push({ from: removedWithInstance.shift(), to: addedLoadedMissing.shift() })
  }
  actions.dispose.push(...removedWithInstance)
  return actions
}

/**
 * True when the planned actions dispose every loaded instance of the element,
 * i.e. after the move it is no longer drawn in any loaded region and whatever
 * selection still points at it must be closed.
 */
export function leavesLoadedScene(regionsWithInstance: string[], actions: RecompartmentActions): boolean {
  const withInstance = new Set(regionsWithInstance ?? [])
  if (withInstance.size === 0) return false
  const disposed = new Set(actions?.dispose ?? [])
  return [...withInstance].every(regionId => disposed.has(regionId))
}
